import { useEffect, useMemo, useRef, useState } from 'react';
import { DateRange } from 'react-day-picker';

import { DateRangePicker } from '@/components/DateRangePicker';
import { Footer } from '@/components/Footer';
import { Navbar } from '@/components/Navbar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';

type PromptRun = {
  id: number;
  article_id: number;
  email: string;
  prompt: string | null;
  identified_symbol: string | null;
  relevant: boolean;
  explanation: string | null;
  full_response: string | null;
  model: string | null;
  created_at: string;
  article_title: string;
  article_source: string;
};

export default function PromptRunsPage() {
  const [runs, setRuns] = useState<PromptRun[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [offset, setOffset] = useState<number>(0);
  const LIMIT = 30;
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const [dateRange, setDateRange] = useState<DateRange | undefined>(undefined);
  const [relevanceFilter, setRelevanceFilter] = useState<
    'toate' | 'relevant' | 'nerelevant'
  >('toate');

  const fromTs = useMemo(
    () => (dateRange?.from ? dateRange.from.getTime() : undefined),
    [dateRange]
  );
  const toTs = useMemo(
    () => (dateRange?.to ? dateRange.to.getTime() : undefined),
    [dateRange]
  );

  async function fetchRuns(reset = false) {
    try {
      setIsLoading(true);
      const params = new URLSearchParams();
      params.set('limit', String(LIMIT));
      params.set('offset', String(reset ? 0 : offset));
      if (fromTs) params.set('from', String(fromTs));
      if (toTs) params.set('to', String(toTs));
      if (relevanceFilter !== 'toate') {
        params.set(
          'relevant',
          relevanceFilter === 'relevant' ? 'true' : 'false'
        );
      }

      const res = await fetch(`/api/prompt-runs?${params.toString()}`);
      if (!res.ok) throw new Error('Failed to load prompt runs');
      const data = await res.json();
      const batch: PromptRun[] = data.promptRuns || [];
      setHasMore(Boolean(data.hasMore));
      setRuns((prev) => (reset ? batch : [...prev, ...batch]));
      setOffset((prev) => (reset ? LIMIT : prev + LIMIT));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load prompt runs');
    } finally {
      setIsLoading(false);
    }
  }

  // Initial load
  useEffect(() => {
    fetchRuns(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-load when dateRange or relevanceFilter changes
  useEffect(() => {
    setOffset(0);
    fetchRuns(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromTs, toTs, relevanceFilter]);

  // Infinite scroll via IntersectionObserver
  useEffect(() => {
    if (!sentinelRef.current) return;
    const el = sentinelRef.current;
    const observer = new IntersectionObserver((entries) => {
      const first = entries[0];
      if (first.isIntersecting && !isLoading && hasMore) {
        fetchRuns();
      }
    });
    observer.observe(el);
    return () => observer.unobserve(el);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, hasMore, sentinelRef.current]);

  if (error) {
    return (
      <div className="mx-auto max-w-5xl p-4">
        <Card>
          <CardContent className="pt-6">
            <h1 className="mb-2 font-heading text-2xl font-bold">
              Istoric Rulări AI
            </h1>
            <p className="text-red-600">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const pageContent = (
    <>
      {runs.length === 0 ? (
        <p>Nu aveți încă nicio rulare de prompt.</p>
      ) : (
        runs.map((r, idx) => {
          // Split comma-separated symbols into array, filtering out "error" values
          const symbols = r.identified_symbol
            ? r.identified_symbol
                .split(',')
                .map((s) => s.trim())
                .filter((s) => s && s.toLowerCase() !== 'error')
            : [];

          return (
            <div key={r.id} className="flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge
                    variant="outline"
                    className={
                      r.relevant
                        ? 'border-green-600 text-green-700'
                        : 'border-red-600 text-red-700'
                    }>
                    {r.relevant ? 'Relevant' : 'Nerelevant'}
                  </Badge>
                  {symbols.length > 0 &&
                    symbols.map((symbol) => (
                      <Badge key={symbol} variant="outline">
                        {symbol}
                      </Badge>
                    ))}
                </div>
                <div className="text-sm text-gray-500">
                  {new Date(r.created_at).toLocaleString('ro-RO')}
                </div>
              </div>
              <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
                <div className="text-sm font-medium text-gray-900">
                  {r.article_title}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="shrink-0"
                  asChild>
                  <a
                    href={r.article_source}
                    target="_blank"
                    rel="noopener noreferrer">
                    Vezi articolul original
                  </a>
                </Button>
              </div>
              <div className="text-sm text-gray-700">
                <span className="font-bold">Alerta:</span> {r.prompt ?? '—'}
              </div>
              <div className="text-sm">
                <span className="font-bold">Explicație:</span>{' '}
                {r.explanation ?? '—'}
              </div>
              {idx < runs.length - 1 && <Separator className="mt-2" />}
            </div>
          );
        })
      )}
    </>
  );

  return (
    <div className="flex min-h-screen flex-col bg-neutral-50 text-neutral-800">
      <Navbar />
      <main className="mx-auto w-full max-w-6xl flex-1 px-4 pb-8 pt-28 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="mb-2 font-heading text-2xl font-bold">
            Istoric Rulări AI
          </h1>
          <p className="mb-6 text-neutral-600">
            Mai jos puteți să vedeți cum au fost analizate stirile bazat pe
            alerta ta.
          </p>
        </div>
        <Card>
          <CardContent className="space-y-4 pt-6">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-end">
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-neutral-700">
                  Relevanță
                </label>
                <Select
                  value={relevanceFilter}
                  onValueChange={(value) =>
                    setRelevanceFilter(
                      value as 'toate' | 'relevant' | 'nerelevant'
                    )
                  }>
                  <SelectTrigger className="h-9 w-full border-neutral-300 bg-white sm:w-[180px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="toate">Toate</SelectItem>
                    <SelectItem value="relevant">Relevant</SelectItem>
                    <SelectItem value="nerelevant">Nerelevant</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <DateRangePicker
                dateRange={dateRange}
                onChange={setDateRange}
                onClear={() => setDateRange(undefined)}
                placeholder="Selectează intervalul"
              />
            </div>
            {pageContent}
            <div ref={sentinelRef} />
            {isLoading && (
              <p className="text-sm text-neutral-500">Se încarcă...</p>
            )}
            {!hasMore && runs.length > 0 && (
              <div className="text-center text-xs text-neutral-500">
                Nu mai sunt rezultate
              </div>
            )}
          </CardContent>
        </Card>
      </main>
      <Footer />
    </div>
  );
}
